/**
 * Monthly Calendar Theme
 *
 * Modern minimal theme for react-native-calendars.
 * Clean whites, subtle shadows, refined typography.
 */

import { Colors, ModernPalette } from '@/constants/theme';
import { getBodyFont, getHeadingFont } from '@/constants/typography';
import { Dimensions } from 'react-native';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

// Horizontal padding for calendar grid (left and right)
export const CALENDAR_HORIZONTAL_PADDING = 8;

// DAY_WIDTH accounts for padding on both sides
export const DAY_WIDTH = Math.floor((SCREEN_WIDTH - (CALENDAR_HORIZONTAL_PADDING * 2)) / 7);

// Calculate height to fit 6 rows in the calendar area
// monthHeader actual height: 14px padding + 36px content + 14px padding + 1px border = 65px
const MONTH_HEADER_HEIGHT = 66; // Month navigation header (65px actual + 1px safety)
const DAY_NAMES_HEIGHT = 36; // Day name row

// Small safety buffer for rounding errors
const BOTTOM_PADDING_BUFFER = 12;

// DAY_HEIGHT remains constant - calculated based on 6-week layout (maximum)
const NUM_ROWS_MAX = 6; // Maximum rows for sizing

// Calendar takes up 90% of screen height (accounting for tab bar)
const CALENDAR_AREA_HEIGHT = SCREEN_HEIGHT * 0.86;
const AVAILABLE_FOR_ROWS = CALENDAR_AREA_HEIGHT - MONTH_HEADER_HEIGHT - DAY_NAMES_HEIGHT;

// Cell height is constant regardless of 5 or 6 weeks shown
export const DAY_HEIGHT = Math.floor(AVAILABLE_FOR_ROWS / NUM_ROWS_MAX);

/**
 * Calculate calendar grid height for a given number of week rows
 * Cell height stays constant - only number of rows changes
 * Includes buffer to prevent event bars in last row from being cut off
 */
export function calculateCalendarGridHeight(numRows: number): number {
  return (numRows * DAY_HEIGHT) + DAY_NAMES_HEIGHT + BOTTOM_PADDING_BUFFER;
}

/**
 * Calculate total calendar height (grid + header)
 * Buffer is already included in grid height
 */
export function calculateCalendarTotalHeight(numRows: number): number {
  return calculateCalendarGridHeight(numRows) + MONTH_HEADER_HEIGHT;
}

// Keep these for backwards compatibility - default to 6 rows
export const CALENDAR_GRID_HEIGHT = calculateCalendarGridHeight(6);
export const CALENDAR_TOTAL_HEIGHT = calculateCalendarTotalHeight(6);

/**
 * Modern event colors (refined, accessible)
 */
export const PaperEventColors = {
  default: ModernPalette.eventBlue,
  pink: ModernPalette.eventPink,
  yellow: ModernPalette.eventYellow,
  orange: ModernPalette.eventOrange,
  lime: ModernPalette.eventLime,
};

/**
 * MonthlyCalendarTheme
 *
 * Provides modern minimal theme configuration.
 */
export class MonthlyCalendarTheme {
  /**
   * Get theme configuration for react-native-calendars Calendar component
   */
  static getTheme(colorScheme: 'light' | 'dark', accentColor: string) {
    const palette = Colors[colorScheme];
    const isLight = colorScheme === 'light';

    return {
      // Background colors - clean white
      backgroundColor: isLight ? ModernPalette.white : palette.background,
      calendarBackground: isLight ? ModernPalette.white : palette.background,

      // Text colors - high contrast
      textSectionTitleColor: isLight ? palette.textMuted : '#FFFFFF',
      selectedDayBackgroundColor: accentColor,
      selectedDayTextColor: '#FFFFFF',
      todayTextColor: palette.primary,
      dayTextColor: palette.text,
      textDisabledColor: isLight ? 'rgba(26, 26, 26, 0.25)' : 'rgba(255, 255, 255, 0.25)',
      monthTextColor: palette.text,

      // Font settings - modern
      textMonthFontWeight: '600',
      textDayFontSize: 14,
      textMonthFontSize: 20,
      textDayHeaderFontSize: 12,

      // Arrow colors
      arrowColor: accentColor,

      // Stylesheet overrides for day cell sizing
      'stylesheet.calendar.main': {
        container: {
          backgroundColor: isLight ? ModernPalette.white : palette.background,
        },
        monthView: {
          backgroundColor: isLight ? ModernPalette.white : palette.background,
        },
        week: {
          marginTop: 0,
          marginBottom: 0,
          flexDirection: 'row',
          justifyContent: 'space-around',
          backgroundColor: isLight ? ModernPalette.white : palette.background,
        },
        dayContainer: {
          width: DAY_WIDTH,
          alignItems: 'center',
          backgroundColor: isLight ? ModernPalette.white : palette.background,
        },
      },
      'stylesheet.calendar.header': {
        header: {
          backgroundColor: isLight ? ModernPalette.white : palette.background,
          flexDirection: 'row',
          justifyContent: 'space-between',
          paddingLeft: 10,
          paddingRight: 10,
          alignItems: 'center',
        },
        partialHeader: {
          paddingHorizontal: 15,
          backgroundColor: isLight ? ModernPalette.white : palette.background,
        },
        headerContainer: {
          flexDirection: 'row',
          backgroundColor: isLight ? ModernPalette.white : palette.background,
        },
        monthText: {
          color: palette.text,
        },
        week: {
          marginTop: 0,
          marginBottom: 0,
          flexDirection: 'row',
          justifyContent: 'space-around',
          backgroundColor: isLight ? ModernPalette.white : palette.background,
          borderTopWidth: 0,
          borderBottomWidth: 0,
        },
        dayHeader: {
          width: DAY_WIDTH,
          textAlign: 'center',
          color: isLight ? palette.textMuted : '#FFFFFF',
          fontSize: 12,
          fontWeight: '500',
          fontFamily: getHeadingFont(),
          backgroundColor: isLight ? ModernPalette.white : palette.background,
        },
        dayTextAtIndex0: { width: DAY_WIDTH, color: isLight ? palette.textMuted : '#FFFFFF' },
        dayTextAtIndex1: { width: DAY_WIDTH, color: isLight ? palette.textMuted : '#FFFFFF' },
        dayTextAtIndex2: { width: DAY_WIDTH, color: isLight ? palette.textMuted : '#FFFFFF' },
        dayTextAtIndex3: { width: DAY_WIDTH, color: isLight ? palette.textMuted : '#FFFFFF' },
        dayTextAtIndex4: { width: DAY_WIDTH, color: isLight ? palette.textMuted : '#FFFFFF' },
        dayTextAtIndex5: { width: DAY_WIDTH, color: isLight ? palette.textMuted : '#FFFFFF' },
        dayTextAtIndex6: { width: DAY_WIDTH, color: isLight ? palette.textMuted : '#FFFFFF' },
      },
      'stylesheet.day.basic': {
        base: {
          width: DAY_WIDTH,
          height: DAY_HEIGHT,
          alignItems: 'center',
          backgroundColor: isLight ? ModernPalette.white : palette.background,
        },
      },
    };
  }

  /**
   * Get custom styles for calendar components (used by CalendarDayCell)
   */
  static getCustomStyles(colorScheme: 'light' | 'dark', accentColor: string) {
    const palette = Colors[colorScheme];
    const isLight = colorScheme === 'light';

    return {
      // Container
      container: {
        backgroundColor: isLight ? ModernPalette.offWhite : palette.background,
      },

      // Month header - modern
      monthHeader: {
        fontSize: 22,
        fontWeight: '600' as const,
        fontFamily: getHeadingFont(),
        color: palette.text,
        textAlign: 'left' as const,
        paddingHorizontal: 16,
        paddingVertical: 16,
      },

      // Day header row - clean
      dayHeaderContainer: {
        backgroundColor: isLight ? ModernPalette.white : palette.backgroundLight,
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: isLight ? ModernPalette.borderLight : palette.border,
      },

      dayHeaderText: {
        fontSize: 12,
        fontWeight: '500' as const,
        fontFamily: getHeadingFont(),
        color: palette.textMuted,
        textTransform: 'uppercase' as const,
        textAlign: 'center' as const,
        letterSpacing: 0.5,
      },

      // Day cells - modern minimal
      dayCell: {
        backgroundColor: isLight ? ModernPalette.white : palette.backgroundLight,
        borderWidth: 0.3,
        borderColor: isLight ? ModernPalette.borderLight : palette.border,
        borderRadius: 3,
        minHeight: 50,
        padding: 2,
        margin: 0.5,
      },

      // Day number - clean
      dayNumber: {
        fontSize: 12,
        fontWeight: '500' as const,
        fontFamily: getBodyFont(),
        color: palette.text,
        textAlign: 'left' as const,
        paddingLeft: 2,
        paddingTop: 1,
      },

      // Today's date - subtle highlight using mode-appropriate accent (blue/orange)
      selectedCell: {
        backgroundColor: isLight ? ModernPalette.todayHighlight : palette.todayHighlight,
        borderRadius: 4,
        borderWidth: 0.8,
        borderColor: palette.primary,
      },

      // Outside month days
      outsideMonthCell: {
        backgroundColor: isLight ? ModernPalette.lightGray : palette.background,
        opacity: 0.5,
      },

      outsideMonthNumber: {
        color: isLight ? 'rgba(26, 26, 26, 0.3)' : 'rgba(255, 255, 255, 0.3)',
        fontWeight: '400' as const,
      },

      // Event text bars - compact to fit 3 events per cell
      eventBar: {
        backgroundColor: ModernPalette.eventBlue,
        borderRadius: 2,
        paddingHorizontal: 2,
        paddingVertical: 2,
        marginTop: 0,
        marginBottom: 0,
        marginHorizontal: 0,
        height: 17,
        flexDirection: 'row' as const,
        alignItems: 'center' as const,
      },

      eventBarText: {
        fontSize: 10,
        lineHeight: 12,
        color: '#FFFFFF',
        fontWeight: '700' as const,
      },

      // Overflow indicator - clean
      overflowIndicator: {
        fontSize: 8,
        fontFamily: getBodyFont(),
        color: palette.textMuted,
        fontWeight: '600' as const,
        textAlign: 'center' as const,
        paddingVertical: 0,
        marginTop: 0,
        height: 12,
      },

      // All-day event bar variant
      allDayEventBar: {
        backgroundColor: isLight ? ModernPalette.primary : ModernPalette.primaryLight,
      },
    };
  }

  /**
   * Get navigation arrow styles
   */
  static getNavigationStyles(colorScheme: 'light' | 'dark') {
    const palette = Colors[colorScheme];

    return {
      leftArrow: {
        color: palette.text,
        fontSize: 24,
      },
      rightArrow: {
        color: palette.text,
        fontSize: 24,
      },
    };
  }
}
